import React, { useEffect, useState } from 'react';
import { Box, TextField, InputAdornment, Typography, IconButton, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { colors } from '@src/styles';
import { ACTION_BUTTON_HEIGHT, filterFieldSX, monoFontFamily } from '../scriptsStyles';
import { useError } from '@src/contexts/ErrorContext';

type Props = {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onSave?: (newMs: number) => Promise<void>;
  isSaving?: boolean;
};

export const SleepTimeEditor: React.FC<Props> = ({ value, min = 1000, max = 600000, step = 100, onSave, isSaving }) => {
  const [inputValue, setInputValue] = useState<number>(value ?? min);
  const [committedValue, setCommittedValue] = useState<number>(value ?? min);
  const [localError, setLocalError] = useState<string>('');
  const { setError } = useError();

  useEffect(() => {
    setInputValue(value ?? min);
    setCommittedValue(value ?? min);
    setLocalError('');
  }, [value, min]);

  const validate = (val: number) => {
    if (isNaN(val)) return 'Valor inválido';
    if (val < min) return `Mínimo ${min} ms`;
    if (val > max) return `Máximo ${max} ms`;
    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setInputValue(val);
    setLocalError(validate(val));
  };

  const commit = async () => {
    const err = validate(inputValue);
    setLocalError(err);
    if (err) return;
    if (inputValue === committedValue) return; // nothing to do
    try {
      if (onSave) {
        await onSave(inputValue);
      }
      setCommittedValue(inputValue);
    } catch (e) {
      setInputValue(committedValue);
      setError({
        title: 'Erro ao salvar tempo de espera',
        message: e?.response?.data?.message || e?.message || 'Não foi possível salvar o tempo de espera',
        statusCode: e?.response?.status,
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TextField
        // type="number"
        size="small"
        value={inputValue}
        onChange={handleChange}
        onBlur={commit}
        inputProps={{ min, max, step, 'aria-label': 'Intervalo entre actions (ms)' }}
        sx={{
          width: 120,
          ...filterFieldSX,
          '& .MuiOutlinedInput-root': {
            ...filterFieldSX['& .MuiOutlinedInput-root'],
            height: ACTION_BUTTON_HEIGHT,
            fontFamily: monoFontFamily,
            fontSize: '12.5px',
            backgroundColor: colors.background.primary,
          },
          '& .MuiOutlinedInput-root.Mui-error fieldset': { borderColor: colors.status.rejected },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted }}>
                ms
              </Typography>
            </InputAdornment>
          ),
        }}
        error={!!localError}
      />
      <Tooltip title="Salvar">
        <span>
          <IconButton
            size="small"
            onClick={commit}
            disabled={!!localError || isSaving}
            aria-label="Salvar intervalo"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '7px',
              color: colors.brand.textSecondary,
              '& svg': { color: 'inherit', fontSize: 16 },
              '&:hover': { color: colors.primary.yellow, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
            }}
          >
            <SaveIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
